import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaPlus, FaTrash } from "react-icons/fa";
import AssetLayoutWrapper from "../../components/AssetLayoutWrapper";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/taxtypelisting";

const AssetTaxMaster = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  /* ---------------------------------- */
  /* localStorage helpers               */
  /* ---------------------------------- */
  const getLS = (key) => {
    try {
      return localStorage.getItem(key) || "";
    } catch {
      return "";
    }
  };

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branchType = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

   // ✅ fetch list
  const fetchList = async (overridePage) => {
    try {
      setLoading(true);

      const pageToLoad = overridePage ?? currentPage;

      const res = await axios.get(`${API_BASE}/list`, {
        params: { page: pageToLoad, pageSize },
        headers: commonHeaders,
      });

      const data = res.data?.data || [];

      const mapped = data.map((r) => ({
        id: r.fld_TaxId ?? r.taxId ?? r.id,
        taxType: r.fld_TaxType ?? r.taxType ?? r.taxtype,
        status:
          r.fld_Status === 0 ||
          r.fld_Status === false ||
          r.fld_IsActive === 0 ||
          r.fld_IsActive === false ||
          r.status === "Inactive"
            ? "Inactive"
            : "Active",
        selected: false,
      }));

      setRows(mapped);
      setTotalRecords(res.data?.totalRecords ?? mapped.length);
      setTotalPages(res.data?.totalPages ?? 1);
      setCurrentPage(pageToLoad);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load Tax Type listing");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonHeaders]);

  const selectedCount = useMemo(
    () => rows.filter((r) => r.selected).length,
    [rows]
  );

  const allSelected = useMemo(
    () => rows.length > 0 && rows.every((r) => r.selected),
    [rows]
  );

  // ✅ Active/Inactive counts for Results Summary panel
  const statusCounts = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        if (r.status === "Active") acc.active += 1;
        else acc.inactive += 1;
        return acc;
      },
      { active: 0, inactive: 0 }
    );
  }, [rows]);

  const handleAddNew = () => {
    navigate("/Asset/AssetTaxMasterEntry");
  };

  const handleEdit = (id) => {
    navigate(`/Asset/AssetTaxMasterEntry?id=${id}`);
  };

  const toggleRow = (id) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, selected: !r.selected } : r))
    );
  };

  const toggleAll = () => {
    const next = !allSelected;
    setRows((prev) => prev.map((r) => ({ ...r, selected: next })));
  };

  const handleDeleteSelected = async () => {
    const ids = rows.filter((r) => r.selected).map((r) => r.id);

    if (ids.length === 0) {
      toast.error("Please select records to delete!");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.post(
        `${API_BASE}/bulkDelete`,
        { ids },
        { headers: commonHeaders }
      );

          toast.success(res.data?.message || "Deleted successfully");
      await fetchList(1);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "Active":
        return isDark
          ? "bg-green-800 text-green-100"
          : "bg-green-100 text-green-800";
      case "Inactive":
        return isDark
          ? "bg-red-800 text-red-100"
          : "bg-red-100 text-red-800";
      default:
        return isDark
          ? "bg-gray-700 text-gray-300"
          : "bg-gray-200 text-gray-700";
    }
  };

  return (
    <AssetLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ASSET TAX MASTER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Asset Tax Type Listing
            </h1>

            <button
              onClick={handleAddNew}
              className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
            >
              <FaPlus />
              Add New
            </button>
          </div>

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
              <thead className={`${tableHeaderClass} text-white`}>
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Sl No</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Tax Type</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold">Status</th>

                  {/* EDIT - SECOND LAST COLUMN */}
                  <th className="px-6 py-4 text-center text-sm font-semibold">Edit</th>

                  {/* SELECT ALL - LAST COLUMN */}
                  <th className="px-6 py-4 text-center text-sm font-semibold w-16">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      className="w-4 h-4 cursor-pointer"
                      title="Select All"
                    />
                  </th>
                </tr>
              </thead>

              <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                {loading ? (
                  <tr>
                    <td colSpan={5} className={`px-6 py-10 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      Loading...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className={`px-6 py-10 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      No records found.
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => (
                    <tr
                      key={r.id}
                      className={`${
                        idx % 2 === 0
                          ? isDark ? "bg-gray-800" : "bg-white"
                          : isDark ? "bg-gray-750" : "bg-gray-50"
                      } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                    >
                      <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                        {idx + 1}
                      </td>

                      <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                        {r.taxType}
                      </td>

                      <td className="px-6 py-4 text-center">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(
                            r.status
                          )}`}
                        >
                          {r.status}
                        </span>
                      </td>

                      {/* EDIT */}
                      <td className="px-6 py-4 text-center">
                        <button
                          type="button"
                          onClick={() => handleEdit(r.id)}
                          className="text-yellow-500 hover:text-yellow-600 text-lg transition"
                          title="Edit"
                        >
                          ✏️
                        </button>
                      </td>

                      {/* SELECT */}
                      <td className="px-6 py-4 text-center">
                        <input
                          type="checkbox"
                          checked={!!r.selected}
                          onChange={() => toggleRow(r.id)}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* ==================== REMOVE BUTTON + PAGINATION ==================== */}
            {rows.length > 0 && (
              <div className="p-4 flex flex-wrap items-center justify-between gap-3">
                <button
                  onClick={handleDeleteSelected}
                  disabled={loading || selectedCount === 0}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow transition ${
                    loading || selectedCount === 0 ? "opacity-50 cursor-not-allowed" : ""
                  } bg-red-600 hover:bg-red-700 text-white`}
                >
                  <FaTrash />
                  Remove
                </button>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => fetchList(currentPage - 1)}
                    disabled={loading || currentPage <= 1}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold shadow transition ${
                      loading || currentPage <= 1 ? "opacity-50 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    Prev
                  </button>

                  <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Page {currentPage} of {totalPages}
                  </span>

                  <button
                    onClick={() => fetchList(currentPage + 1)}
                    disabled={loading || currentPage >= totalPages}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold shadow transition ${
                      loading || currentPage >= totalPages ? "opacity-50 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ==================== RESULTS SUMMARY ==================== */}
          {rows.length > 0 && (
            <div
              className={`mt-6 p-4 rounded-lg border ${
                isDark ? "bg-gray-800/30 border-gray-600" : "bg-gray-50 border-gray-200"
              }`}
            >
              <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                <div>
                  <h4 className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                    Search Results
                  </h4>
                                 <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                    Showing {rows.length} of {totalRecords} record(s) | Selected: {selectedCount}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className={`${isDark ? "bg-green-900/30" : "bg-green-50"} p-3 rounded-lg`}>
                    <div className="text-lg font-bold text-green-600">{statusCounts.active}</div>
                    <div className={`text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      Active
                    </div>
                  </div>

                  <div className={`${isDark ? "bg-red-900/30" : "bg-red-50"} p-3 rounded-lg`}>
                    <div className="text-lg font-bold text-red-600">{statusCounts.inactive}</div>
                    <div className={`text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      Inactive
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AssetLayoutWrapper>
  );
};

export default AssetTaxMaster;